import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function EditDigitalPropertiesModal({ clientId, currentProperties, isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    primary_website_url: currentProperties?.primary_website_url || '',
    cms_platform: currentProperties?.cms_platform || '',
    hosting_provider: currentProperties?.hosting_provider || '',
    domain_registrar: currentProperties?.domain_registrar || '',
    google_ads_account_id: currentProperties?.google_ads_account_id || '',
    meta_business_manager_id: currentProperties?.meta_business_manager_id || '',
    google_analytics_property_id: currentProperties?.google_analytics_property_id || '',
    google_tag_manager_container_id: currentProperties?.google_tag_manager_container_id || '',
    other_platform_name: currentProperties?.other_platform_name || '',
    other_platform_value: currentProperties?.other_platform_value || '',
    login_url: currentProperties?.login_url || '',
    login_username: currentProperties?.login_username || '',
    login_notes: currentProperties?.login_notes || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [otherHosting, setOtherHosting] = useState('');
  const [otherRegistrar, setOtherRegistrar] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      setSaving(true);
      const response = await fetch(`${API}/api/admin/clients/${clientId}/digital-properties`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSuccess();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to update properties');
      }
    } catch (error) {
      console.error('Error updating properties:', error);
      setError('Failed to update properties');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white flex items-center justify-between p-4 border-b z-10">
            <h3 className="text-lg font-semibold">Edit Digital Properties</h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {error}
              </div>
            )}

            {/* Website & Platform */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900">Website & Platform</h4>
              
              <div>
                <Label htmlFor="primary_website_url">Website URL</Label>
                <Input
                  id="primary_website_url"
                  type="url"
                  value={formData.primary_website_url}
                  onChange={(e) => setFormData({ ...formData, primary_website_url: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <Label htmlFor="cms_platform">CMS Platform</Label>
                <Select value={formData.cms_platform} onValueChange={(value) => setFormData({ ...formData, cms_platform: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select platform..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="WordPress">WordPress</SelectItem>
                    <SelectItem value="Wix">Wix</SelectItem>
                    <SelectItem value="Squarespace">Squarespace</SelectItem>
                    <SelectItem value="Webflow">Webflow</SelectItem>
                    <SelectItem value="Shopify">Shopify</SelectItem>
                    <SelectItem value="Custom">Custom</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="hosting_provider">Hosting Provider</Label>
                  <Select 
                    value={formData.hosting_provider} 
                    onValueChange={(value) => {
                      if (value === 'Other') {
                        setOtherHosting('');
                      }
                      setFormData({ ...formData, hosting_provider: value });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bluehost">Bluehost</SelectItem>
                      <SelectItem value="WP Engine">WP Engine</SelectItem>
                      <SelectItem value="SiteGround">SiteGround</SelectItem>
                      <SelectItem value="HostGator">HostGator</SelectItem>
                      <SelectItem value="GoDaddy">GoDaddy</SelectItem>
                      <SelectItem value="Kinsta">Kinsta</SelectItem>
                      <SelectItem value="AWS">AWS</SelectItem>
                      <SelectItem value="Google Cloud">Google Cloud</SelectItem>
                      <SelectItem value="Cloudflare">Cloudflare</SelectItem>
                      <SelectItem value="Other">Other (specify below)</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.hosting_provider === 'Other' && (
                    <div className="mt-2">
                      <Input
                        placeholder="Specify hosting provider..."
                        value={otherHosting}
                        onChange={(e) => {
                          setOtherHosting(e.target.value);
                          setFormData({ ...formData, hosting_provider: e.target.value });
                        }}
                      />
                    </div>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="domain_registrar">Domain Registrar</Label>
                  <Select 
                    value={formData.domain_registrar} 
                    onValueChange={(value) => {
                      if (value === 'Other') {
                        setOtherRegistrar('');
                      }
                      setFormData({ ...formData, domain_registrar: value });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select registrar..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GoDaddy">GoDaddy</SelectItem>
                      <SelectItem value="Namecheap">Namecheap</SelectItem>
                      <SelectItem value="Google Domains">Google Domains</SelectItem>
                      <SelectItem value="Cloudflare">Cloudflare</SelectItem>
                      <SelectItem value="Name.com">Name.com</SelectItem>
                      <SelectItem value="Domain.com">Domain.com</SelectItem>
                      <SelectItem value="Hover">Hover</SelectItem>
                      <SelectItem value="Other">Other (specify below)</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.domain_registrar === 'Other' && (
                    <div className="mt-2">
                      <Input
                        placeholder="Specify domain registrar..."
                        value={otherRegistrar}
                        onChange={(e) => {
                          setOtherRegistrar(e.target.value);
                          setFormData({ ...formData, domain_registrar: e.target.value });
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Ad & Analytics IDs */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-semibold text-gray-900">Ad & Analytics IDs</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="google_ads_account_id">Google Ads Account ID</Label>
                  <Input
                    id="google_ads_account_id"
                    value={formData.google_ads_account_id}
                    onChange={(e) => setFormData({ ...formData, google_ads_account_id: e.target.value })}
                    placeholder="123-456-7890"
                  />
                </div>
                <div>
                  <Label htmlFor="meta_business_manager_id">Meta Business Manager ID</Label>
                  <Input
                    id="meta_business_manager_id"
                    value={formData.meta_business_manager_id}
                    onChange={(e) => setFormData({ ...formData, meta_business_manager_id: e.target.value })}
                    placeholder="123456789012345"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="google_analytics_property_id">Google Analytics 4 Property ID</Label>
                  <Input
                    id="google_analytics_property_id"
                    value={formData.google_analytics_property_id}
                    onChange={(e) => setFormData({ ...formData, google_analytics_property_id: e.target.value })}
                    placeholder="G-XXXXXXXXXX"
                  />
                </div>
                <div>
                  <Label htmlFor="google_tag_manager_container_id">GTM Container ID</Label>
                  <Input
                    id="google_tag_manager_container_id"
                    value={formData.google_tag_manager_container_id}
                    onChange={(e) => setFormData({ ...formData, google_tag_manager_container_id: e.target.value })}
                    placeholder="GTM-XXXXXX"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="other_platform_name">Other Platform Name</Label>
                  <Input
                    id="other_platform_name"
                    value={formData.other_platform_name}
                    onChange={(e) => setFormData({ ...formData, other_platform_name: e.target.value })}
                    placeholder="e.g., TikTok Ads"
                  />
                </div>
                <div>
                  <Label htmlFor="other_platform_value">Other Platform ID/Value</Label>
                  <Input
                    id="other_platform_value"
                    value={formData.other_platform_value}
                    onChange={(e) => setFormData({ ...formData, other_platform_value: e.target.value })}
                    placeholder="Account ID or reference"
                  />
                </div>
              </div>
            </div>

            {/* Login & Access */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-semibold text-gray-900">Login & Access</h4>
              <p className="text-sm text-gray-600">Note: Do not store raw passwords. Use references to secure storage.</p>
              
              <div>
                <Label htmlFor="login_url">Login URL</Label>
                <Input
                  id="login_url"
                  type="url"
                  value={formData.login_url}
                  onChange={(e) => setFormData({ ...formData, login_url: e.target.value })}
                  placeholder="https://example.com/wp-admin"
                />
              </div>

              <div>
                <Label htmlFor="login_username">Username / Email</Label>
                <Input
                  id="login_username"
                  value={formData.login_username}
                  onChange={(e) => setFormData({ ...formData, login_username: e.target.value })}
                  placeholder="admin@example.com"
                />
              </div>

              <div>
                <Label htmlFor="login_notes">Login Notes</Label>
                <Textarea
                  id="login_notes"
                  value={formData.login_notes}
                  onChange={(e) => setFormData({ ...formData, login_notes: e.target.value })}
                  placeholder="e.g., Credentials stored in 1Password, request access from John Smith"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-6 border-t">
              <Button type="button" variant="outline" onClick={onClose} disabled={saving} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="flex-1">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
